import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import StudentNavigation from '../../components/ui/StudentNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { instituteService } from '../../services/instituteService';
import { chatService } from '../../services/chatService';

const ExploreInstitutions = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [startingChat, setStartingChat] = useState(null);

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'engineering', label: 'Engineering' },
    { value: 'medical', label: 'Medical' },
    { value: 'management', label: 'Management' },
    { value: 'commerce', label: 'Commerce' },
    { value: 'science', label: 'Science' },
    { value: 'arts', label: 'Arts' },
    { value: 'law', label: 'Law' },
    { value: 'professional', label: 'Professional' },
  ];

  useEffect(() => {
    loadInstitutes();
  }, [selectedCategory]);

  const loadInstitutes = async () => {
    try {
      setLoading(true);
      const filters = selectedCategory !== 'all' ? { category: selectedCategory } : {};
      const data = await instituteService?.getInstitutes(filters);
      setInstitutes(data);
    } catch (err) {
      setError(err?.message || 'Failed to load institutes');
    } finally {
      setLoading(false);
    }
  };

  const handleStartConversation = async (instituteId) => {
    if (!user?.id) {
      setError('Please log in to start a conversation');
      return;
    }

    try {
      setStartingChat(instituteId);
      const conversation = await chatService?.createConversation(user?.id, instituteId);
      navigate('/chat-interface');
    } catch (err) {
      setError(err?.message || 'Failed to start conversation');
    } finally {
      setStartingChat(null);
    }
  };

  const filteredInstitutes = institutes?.filter(institute => {
    const matchesSearch = institute?.instituteName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                          institute?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="min-h-screen bg-background">
      <StudentNavigation />
      <div className="pt-[60px] px-4 md:px-6 lg:px-8 py-6 md:py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 md:mb-8">
            <button
              onClick={() => navigate('/chat-interface')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-smooth"
            >
              <Icon name="ArrowLeft" size={20} />
              <span className="text-sm">Back to Conversations</span>
            </button>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              Explore Institutions
            </h1>
            <p className="text-muted-foreground">
              Browse institutions and start conversations with their representatives
            </p>
          </div>

          {/* Filters */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Icon
                    name="Search"
                    size={20}
                    color="var(--color-muted-foreground)"
                    className="absolute left-3 top-1/2 transform -translate-y-1/2"
                  />
                  <input
                    type="text"
                    placeholder="Search institutions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div className="w-full md:w-64">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e?.target?.value)}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {categories?.map(cat => (
                    <option key={cat?.value} value={cat?.value}>
                      {cat?.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
              <Icon name="AlertCircle" size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading institutions...</p>
              </div>
            </div>
          )}

          {/* Institutions Grid */}
          {!loading && filteredInstitutes?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredInstitutes?.map(institute => (
                <div
                  key={institute?.id}
                  className="bg-card border border-border rounded-lg hover:shadow-lg transition-smooth"
                >
                  {/* Banner */}
                  <div className="h-48 bg-gradient-to-r from-primary/20 to-primary/10 relative overflow-hidden rounded-t-lg">
                    {institute?.bannerUrl ? (
                      <img
                        src={institute?.bannerUrl}
                        alt={`${institute?.instituteName} banner`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon name="Building2" size={48} color="var(--color-primary)" />
                      </div>
                    )}
                  </div>

                  {/* Logo */}
                  <div className="px-4 -mt-8 mb-4 relative z-10">
                    <div className="w-20 h-20 bg-card border-4 border-card rounded-lg overflow-hidden shadow-md">
                      {institute?.logoUrl ? (
                        <img
                          src={institute?.logoUrl}
                          alt={`${institute?.instituteName} logo`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-primary/10 flex items-center justify-center">
                          <Icon name="Building2" size={32} color="var(--color-primary)" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="px-4 pb-4">
                    <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-1">
                      {institute?.instituteName}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {institute?.description || 'No description available'}
                    </p>

                    {/* Location */}
                    {institute?.user?.city && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <Icon name="MapPin" size={16} />
                        <span>
                          {institute?.user?.city}
                          {institute?.user?.state && `, ${institute?.user?.state}`}
                        </span>
                      </div>
                    )}

                    {/* Courses Count */}
                    {institute?.courses?.length > 0 && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                        <Icon name="BookOpen" size={16} />
                        <span>{institute?.courses?.length} courses available</span>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        iconName="MessageSquare"
                        iconPosition="left"
                        onClick={() => handleStartConversation(institute?.userId)}
                        disabled={startingChat === institute?.userId}
                        className="flex-1"
                      >
                        {startingChat === institute?.userId ? 'Starting...' : 'Start Chat'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Eye"
                        onClick={() => navigate(`/virtual-booth/${institute?.id}`)}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredInstitutes?.length === 0 && (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto bg-muted rounded-full flex items-center justify-center mb-4">
                <Icon name="Building2" size={40} color="var(--color-muted-foreground)" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                No Institutions Found
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery || selectedCategory !== 'all' ?'Try adjusting your filters or search query' :'No institutions are currently available'}
              </p>
              {(searchQuery || selectedCategory !== 'all') && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('all');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExploreInstitutions;